import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  cancellations,
  claims,
  endorsements,
  type EndorsementKind,
  type Policy,
} from '@/shared/api/resources';
import { formatMoney, moneyInputError, toMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { MotivoPadronizado } from '../shared/MotivoPadronizado';

/**
 * Solicitacoes que nascem sobre uma apolice.
 *
 * Endosso, cancelamento e sinistro sempre se referem a uma garantia especifica, e
 * por isso comecam na apolice, onde ela nao e ambigua. As filas de decisao vivem
 * nos modulos proprios, em `AftercarePages`.
 *
 * Cada dialogo e montado somente enquanto esta aberto. Assim o formulario nasce
 * vazio a cada abertura sem que o componente precise reiniciar o proprio estado.
 */

const tiposDeEndosso: { value: EndorsementKind; label: string }[] = [
  { value: 'valor', label: 'Valor garantido' },
  { value: 'prazo', label: 'Prazo de vigencia' },
  { value: 'tomador', label: 'Troca de tomador' },
  { value: 'dados', label: 'Correcao de dados' },
];

/** Invalida tudo o que a decisao ou a solicitacao pode ter mudado. */
function useInvalidacao() {
  const queryClient = useQueryClient();
  return (chaves: string[]) => {
    for (const chave of chaves) {
      void queryClient.invalidateQueries({ queryKey: [chave] });
    }
  };
}

/**
 * Solicitacao simplificada de endosso pelo tipo de antes (valor, prazo, tomador e
 * dados), que nasce solicitada e e decidida em /endossos.
 *
 * Continua ao lado do assistente de endosso (P-END-1): o endosso de alteracao do
 * assistente guarda os campos, mas o envio espera o calculo do P-END-2, e o SeguraCore
 * mantem o que ja fazia.
 */
export function SimplifiedEndorsementDialog({
  policy,
  open,
  onClose,
}: {
  policy: Policy | null;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidar = useInvalidacao();

  const [tipo, setTipo] = useState<EndorsementKind>('valor');
  const [motivo, setMotivo] = useState('');
  const [diferenca, setDiferenca] = useState('');
  const [novaData, setNovaData] = useState('');

  const solicitar = useMutation({
    mutationFn: () => {
      if (!policy) throw new Error('Apolice nao informada.');
      return endorsements.request(policy.id, {
        kind: tipo,
        reason: motivo,
        amount_delta: tipo === 'valor' ? toMoney(diferenca || '0') : null,
        new_coverage_end: tipo === 'prazo' ? novaData : null,
      });
    },
    onSuccess: () => {
      toast.success('Endosso solicitado. A alteracao vale apos a aprovacao.');
      invalidar(['endorsements', 'policies']);
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const faltaDado =
    motivo.trim() === '' ||
    (tipo === 'valor' && toMoney(diferenca || '0').amount === 0) ||
    (tipo === 'prazo' && novaData === '');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Solicitar endosso"
      description="A alteracao passa por aprovacao antes de valer na apolice."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            loading={solicitar.isPending}
            disabled={faltaDado}
            onClick={() => solicitar.mutate()}
          >
            Solicitar endosso
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Select
          label="Tipo do endosso"
          options={tiposDeEndosso}
          value={tipo}
          onChange={(event) => setTipo(event.target.value as EndorsementKind)}
        />

        {tipo === 'valor' ? (
          <MoneyField
            label="Diferenca no valor garantido (R$)"
            required
            value={diferenca}
            error={moneyInputError(diferenca)}
            hint={
              policy
                ? `Valor atual: ${formatMoney(policy.insured_amount)}. Use sinal negativo para reduzir.`
                : undefined
            }
            onValueChange={setDiferenca}
          />
        ) : null}

        {tipo === 'prazo' ? (
          <Field
            label="Novo fim de vigencia"
            type="date"
            required
            value={novaData}
            onChange={(event) => setNovaData(event.target.value)}
          />
        ) : null}

        {tipo === 'tomador' || tipo === 'dados' ? (
          <Alert tone="info" title="Alteracao sem efeito financeiro">
            O endosso registra a alteracao contratual. O dado corrigido continua vivendo
            no cadastro.
          </Alert>
        ) : null}

        <Textarea
          label="Motivo do endosso"
          required
          value={motivo}
          hint="Fica registrado na apolice e na trilha de auditoria."
          onChange={(event) => setMotivo(event.target.value)}
        />
      </div>
    </Modal>
  );
}

export function CancellationRequestDialog({
  policy,
  open,
  onClose,
}: {
  policy: Policy | null;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidar = useInvalidacao();
  const [motivo, setMotivo] = useState('');
  // Confirmacao do OnPoint antes do envio (END-138): o cancelamento tem efeito
  // permanente quando aprovado.
  const [confirmando, setConfirmando] = useState(false);

  const solicitar = useMutation({
    mutationFn: () => {
      if (!policy) throw new Error('Apolice nao informada.');
      return cancellations.request(policy.id, motivo);
    },
    onSuccess: () => {
      toast.success(
        'Solicitacao de cancelamento enviada com sucesso! A garantia vale ate a aprovacao.',
      );
      invalidar(['cancellations', 'policies']);
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (confirmando) {
    return (
      <Modal
        open={open}
        onClose={() => setConfirmando(false)}
        title="Atencao!"
        description="Essa acao tem efeito permanente, deseja continuar?"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmando(false)}>
              Voltar
            </Button>
            <Button
              variant="danger"
              loading={solicitar.isPending}
              onClick={() => solicitar.mutate()}
            >
              Confirmar
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-700">Motivo: {motivo}</p>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Solicitar cancelamento"
      description="Quem pede e quem aprova sao papeis diferentes: a apolice continua vigente ate a decisao."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            variant="danger"
            disabled={motivo.trim() === ''}
            onClick={() => setConfirmando(true)}
          >
            Solicitar cancelamento
          </Button>
        </>
      }
    >
      <MotivoPadronizado
        escopo="cancelamento"
        label="Motivo do cancelamento"
        value={motivo}
        hint="Fica registrado na solicitacao, na apolice e na trilha de auditoria."
        onChange={setMotivo}
      />
    </Modal>
  );
}

export function ClaimRegisterDialog({
  policy,
  open,
  onClose,
}: {
  policy: Policy | null;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidar = useInvalidacao();

  const [natureza, setNatureza] = useState('');
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [ocorrencia, setOcorrencia] = useState('');

  const registrar = useMutation({
    mutationFn: () => {
      if (!policy) throw new Error('Apolice nao informada.');
      return claims.register(policy.id, {
        kind: natureza,
        description: descricao,
        claimed_amount: toMoney(valor || '0'),
        occurrence_date: ocorrencia || null,
      });
    },
    onSuccess: () => {
      toast.success('Sinistro registrado.');
      invalidar(['claims', 'policies']);
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const faltaDado =
    natureza.trim() === '' ||
    descricao.trim() === '' ||
    toMoney(valor || '0').amount <= 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Registrar sinistro"
      description="O acionamento da garantia entra aberto e passa pela analise antes de qualquer pagamento."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            loading={registrar.isPending}
            disabled={faltaDado}
            onClick={() => registrar.mutate()}
          >
            Registrar sinistro
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Natureza do acionamento"
          required
          value={natureza}
          placeholder="Inadimplemento contratual"
          onChange={(event) => setNatureza(event.target.value)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <MoneyField
            label="Valor reclamado (R$)"
            required
            value={valor}
            error={moneyInputError(valor)}
            hint={
              policy ? `Garantia de ${formatMoney(policy.insured_amount)}.` : undefined
            }
            onValueChange={setValor}
          />
          <Field
            label="Data do evento"
            type="date"
            value={ocorrencia}
            hint="Deixe vazio quando ainda nao se conhece a data."
            onChange={(event) => setOcorrencia(event.target.value)}
          />
        </div>

        <Textarea
          label="Descricao do sinistro"
          required
          value={descricao}
          onChange={(event) => setDescricao(event.target.value)}
        />
      </div>
    </Modal>
  );
}
